/**
 * ZERO Makeup — Store Locator
 * Modules: Utils, StoreData, FilterManager, MapManager (MapLibre/Leaflet adapters,
 *          Google-Maps-style info popup), StoreLocator (orchestrator).
 * No build step: plain ES2017+, one file, section-scoped, Theme-Editor safe.
 */
(function () {
  "use strict";

  /* ============================================================
   * UTILS
   * ============================================================ */
  const Utils = {
    ready(fn) {
      if (document.readyState === "complete" || document.readyState === "interactive") {
        setTimeout(fn, 0);
      } else {
        document.addEventListener("DOMContentLoaded", fn);
      }
    },
    escapeHtml(str) {
      return String(str ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },
    slugify(str) {
      return String(str ?? "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    },
    debounce(fn, wait) {
      let t = null;
      const debounced = (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), wait);
      };
      debounced.cancel = () => clearTimeout(t);
      return debounced;
    },
    haversineKm(lat1, lng1, lat2, lng2) {
      const toRad = (d) => (d * Math.PI) / 180;
      const dLat = toRad(lat2 - lat1);
      const dLng = toRad(lng2 - lng1);
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
      return 6371 * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
    },
    formatDistance(km) {
      if (!Number.isFinite(km)) return "";
      if (km < 1) return `${Math.round(km * 1000)} m away`;
      return `${km.toFixed(1)} km away`;
    },
    safeJsonParse(el, fallback) {
      try {
        const txt = el?.textContent?.trim();
        return txt ? JSON.parse(txt) : fallback;
      } catch {
        return fallback;
      }
    },
    uniqSorted(arr) {
      return Array.from(new Set(arr.filter(Boolean))).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "base" })
      );
    },
    titleCase(str) {
      return String(str ?? "")
        .replace(/[-_]+/g, " ")
        .replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase());
    },
    prefersReducedMotion() {
      return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    },
    // "10:00-22:00" -> "10:00 AM - 10:00 PM"
    formatTimeRange(range) {
      if (!range || /closed/i.test(range)) return "Closed";
      return range
        .split(",")
        .map((part) => {
          const [start, end] = part.split("-").map((s) => s.trim());
          if (!start || !end) return part;
          return `${Utils.formatTime(start)} – ${Utils.formatTime(end)}`;
        })
        .join(", ");
    },
    formatTime(hhmm) {
      const [h, m] = hhmm.split(":").map(Number);
      if (!Number.isFinite(h)) return hhmm;
      const period = h >= 12 ? "PM" : "AM";
      const h12 = h % 12 === 0 ? 12 : h % 12;
      return `${h12}:${String(m || 0).padStart(2, "0")} ${period}`;
    },
  };

  const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

  function computeOpenStatus(hours) {
    if (!hours || typeof hours !== "object") return null;
    const now = new Date();
    const todayKey = DAY_KEYS[now.getDay()];
    const range = hours[todayKey];
    if (!range || /closed/i.test(range)) return { isOpen: false, label: "Closed today" };
    const [start, end] = String(range).split(",")[0].split("-").map((s) => s.trim());
    if (!start || !end) return null;
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    const mins = now.getHours() * 60 + now.getMinutes();
    const startMins = sh * 60 + sm;
    const endMins = eh * 60 + em;
    const isOpen = mins >= startMins && mins < endMins;
    return {
      isOpen,
      label: isOpen
        ? `Open now · Closes ${Utils.formatTime(end)}`
        : mins < startMins
        ? `Closed · Opens ${Utils.formatTime(start)}`
        : "Closed now",
    };
  }

  /* ============================================================
   * STORE DATA — normalization (backward compatible)
   * ============================================================ */
  const KNOWN_CATEGORIES = ["official", "retail-partner", "salon", "n-gents", "department-store"];

  function normalizeStore(raw, index) {
    const id = raw.id || Utils.slugify(raw.name) || `store-${index}`;
    const isOfficial = raw.isOfficial === true || String(raw.type || "").toLowerCase() === "official";
    const rawType = Utils.slugify(raw.type || "");
    const category = isOfficial
      ? "official"
      : KNOWN_CATEGORIES.includes(rawType)
      ? rawType
      : "retail-partner";

    const lat = Number(raw.lat);
    const lng = Number(raw.lng);

    return {
      id,
      slug: raw.slug || id,
      name: raw.name || "",
      address: raw.address || "",
      area: raw.area || "",
      city: raw.city || "",
      province: raw.province || "",
      country: raw.country || "",
      postalCode: raw.postalCode || "",
      category,
      isOfficial: category === "official",
      subtype: raw.type || "",
      phone: raw.phone || "",
      hours: raw.hours || "", // string (legacy) OR structured object {mon:"10:00-22:00",...}
      lat,
      lng,
      googleMapsUrl: raw.googleMapsUrl || "",
      googlePlaceId: raw.googlePlaceId || "",
      image: raw.image || "",
      website: raw.website || "",
      tags: Array.isArray(raw.tags) ? raw.tags : [],
      valid: Number.isFinite(lat) && Number.isFinite(lng) && !!raw.name,
    };
  }

  function buildDirectionsUrl(store) {
    if (store.googleMapsUrl) return store.googleMapsUrl;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${store.lat},${store.lng}`)}`;
  }

  /* ============================================================
   * STATE STORE — single source of truth + pub/sub
   * ============================================================ */
  class StateStore {
    constructor(initial) {
      this.state = { ...initial };
      this.listeners = new Set();
    }
    get() {
      return this.state;
    }
    set(patch) {
      this.state = { ...this.state, ...patch };
      this.listeners.forEach((fn) => fn(this.state));
    }
    subscribe(fn) {
      this.listeners.add(fn);
      return () => this.listeners.delete(fn);
    }
  }

  /* ============================================================
   * FILTER MANAGER — search, city, type, sort
   * ============================================================ */
  class FilterManager {
    constructor(stores) {
      this.stores = stores;
      this.fuse =
        window.Fuse &&
        new window.Fuse(stores, {
          keys: [
            { name: "name", weight: 0.4 },
            { name: "area", weight: 0.2 },
            { name: "city", weight: 0.2 },
            { name: "address", weight: 0.15 },
            { name: "tags", weight: 0.05 },
          ],
          threshold: 0.32,
          ignoreLocation: true,
        });
    }

    getFilteredSorted({ query, city, category, userLoc }) {
      let list = this.stores;
      const q = (query || "").trim();

      if (q && this.fuse) {
        list = this.fuse.search(q).map((r) => r.item);
      }
      if (city) list = list.filter((s) => Utils.slugify(s.city) === city);
      if (category) {
        list = list.filter((s) =>
          category === "retail-partner" ? s.category !== "official" : s.category === category
        );
      }

      if (userLoc) {
        list = list.map((s) => ({
          ...s,
          distanceKm: Utils.haversineKm(userLoc.lat, userLoc.lng, s.lat, s.lng),
        }));
      } else {
        list = list.map((s) => ({ ...s, distanceKm: null }));
      }

      // Sorting: relevance (search) > distance (near me) > official-first alphabetical
      if (!q) {
        if (userLoc) {
          list = [...list].sort((a, b) => (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity));
        } else {
          list = [...list].sort(
            (a, b) => Number(b.isOfficial) - Number(a.isOfficial) || a.name.localeCompare(b.name)
          );
        }
      }
      return list;
    }

    cities() {
      return Utils.uniqSorted(this.stores.map((s) => s.city));
    }
  }

  /* ============================================================
   * MARKER DOM BUILDER (shared by both map adapters)
   * ============================================================ */
  function buildMarkerEl(store, { active = false, iconUrl, iconWidth, iconHeight } = {}) {
    const el = document.createElement("div");
    const isOfficial = store.isOfficial;
    const useCustomIcon = !!iconUrl;
    el.className = [
      "zl-marker",
      useCustomIcon ? "zl-marker--custom" : isOfficial ? "zl-marker--official" : "zl-marker--partner",
      active ? "is-active" : "",
    ]
      .filter(Boolean)
      .join(" ");
    el.setAttribute("role", "button");
    el.setAttribute("tabindex", "0");
    el.setAttribute("aria-label", `${store.name}${isOfficial ? ", Official Store" : ", Retail Partner"}`);

    if (useCustomIcon) {
      // Same icon for every store (official and partner alike) — the merchant's
      // single custom pin image, sized via the custom_pin_width/height settings.
      const w = iconWidth || 32;
      const h = iconHeight || 40;
      el.innerHTML = `<span class="zl-marker__custom" style="width:${w}px;height:${h}px"><img src="${Utils.escapeHtml(
        iconUrl
      )}" alt="" width="${w}" height="${h}" loading="lazy"></span>`;
    } else {
      el.innerHTML = isOfficial
        ? `<span class="zl-marker__pin zl-marker__pin--official"><span class="zl-marker__mono">Z</span></span>`
        : `<span class="zl-marker__pin zl-marker__pin--partner"></span>`;
    }
    return el;
  }

  function buildClusterEl(count) {
    const el = document.createElement("div");
    el.className = "zl-marker zl-marker--cluster";
    el.setAttribute("role", "button");
    el.setAttribute("tabindex", "0");
    el.setAttribute("aria-label", `${count} stores in this area, activate to zoom in`);
    el.innerHTML = `<span class="zl-marker__cluster">${count}</span>`;
    return el;
  }

  /* ============================================================
   * SCREEN-SPACE CLUSTERING (works identically for either map engine)
   * ============================================================ */
  function clusterByPixel(stores, projectFn, thresholdPx = 46) {
    const points = stores
      .map((s) => {
        const p = projectFn(s);
        return p ? { store: s, x: p.x, y: p.y } : null;
      })
      .filter(Boolean);

    const clusters = [];
    const used = new Array(points.length).fill(false);

    for (let i = 0; i < points.length; i++) {
      if (used[i]) continue;
      const group = [points[i]];
      used[i] = true;
      for (let j = i + 1; j < points.length; j++) {
        if (used[j]) continue;
        const dx = points[i].x - points[j].x;
        const dy = points[i].y - points[j].y;
        if (Math.sqrt(dx * dx + dy * dy) <= thresholdPx) {
          group.push(points[j]);
          used[j] = true;
        }
      }
      clusters.push(group);
    }

    return clusters.map((group) => ({
      count: group.length,
      stores: group.map((g) => g.store),
      lat: group.reduce((sum, g) => sum + g.store.lat, 0) / group.length,
      lng: group.reduce((sum, g) => sum + g.store.lng, 0) / group.length,
    }));
  }

  /* ============================================================
   * MAP ADAPTERS
   * ============================================================ */
  class MapLibreAdapter {
    constructor(config) {
      this.config = config;
      this.map = null;
      this.markers = new Map(); // storeId -> maptilersdk.Marker
      this.userMarker = null;
      this.popup = null;
      this.onMarkerActivate = null;
      this.onClusterActivate = null;
    }

    async init(container) {
      const sdk = window.maptilersdk;
      sdk.config.apiKey = this.config.maptilerApiKey;
      this.map = new sdk.Map({
        container,
        style: this.config.maptilerStyle
          ? sdk.MapStyle[this.config.maptilerStyle.toUpperCase()] || this.config.maptilerStyle
          : sdk.MapStyle.STREETS,
        language: sdk.Language.ENGLISH,
        center: [this.config.defaultLng, this.config.defaultLat],
        zoom: this.config.defaultZoom,
        geolocateControl: false,
        navigationControl: "top-right",
        cooperativeGestures: true,
      });
      await new Promise((resolve) => this.map.on("load", resolve));
      return this;
    }

    project(store) {
      if (!this.map) return null;
      const p = this.map.project([store.lng, store.lat]);
      return { x: p.x, y: p.y };
    }

    onMove(cb) {
      this.map.on("moveend", cb);
      this.map.on("zoomend", cb);
    }

    setMarkers(items, activeId) {
      this.markers.forEach((m) => m.remove());
      this.markers.clear();

      items.forEach((item) => {
        const sdk = window.maptilersdk;
        if (item.count > 1) {
          const el = buildClusterEl(item.count);
          const marker = new sdk.Marker({ element: el }).setLngLat([item.lng, item.lat]).addTo(this.map);
          const activate = () => this.onClusterActivate && this.onClusterActivate(item);
          el.addEventListener("click", activate);
          el.addEventListener("keydown", (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              activate();
            }
          });
          this.markers.set(`cluster-${item.lat}-${item.lng}`, marker);
        } else {
          const store = item.stores[0];
          const el = buildMarkerEl(store, {
            active: store.id === activeId,
            iconUrl: this.config.customPinIconUrl,
            iconWidth: this.config.customPinWidth,
            iconHeight: this.config.customPinHeight,
          });
          const marker = new sdk.Marker({ element: el, anchor: "bottom" })
            .setLngLat([store.lng, store.lat])
            .addTo(this.map);
          const activate = () => this.onMarkerActivate && this.onMarkerActivate(store.id);
          el.addEventListener("click", activate);
          el.addEventListener("keydown", (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              activate();
            }
          });
          this.markers.set(store.id, marker);
        }
      });
    }

    flyTo(lat, lng, zoom) {
      this.map.flyTo({ center: [lng, lat], zoom, essential: true, duration: Utils.prefersReducedMotion() ? 0 : 700 });
    }

    fitBounds(stores, { maxZoom = 15, padding = 60 } = {}) {
      if (!stores.length) return;
      if (stores.length === 1) {
        this.flyTo(stores[0].lat, stores[0].lng, maxZoom);
        return;
      }
      const lats = stores.map((s) => s.lat);
      const lngs = stores.map((s) => s.lng);
      this.map.fitBounds(
        [
          [Math.min(...lngs), Math.min(...lats)],
          [Math.max(...lngs), Math.max(...lats)],
        ],
        { padding, maxZoom, duration: Utils.prefersReducedMotion() ? 0 : 700 }
      );
    }

    setUserLocation(lat, lng) {
      const sdk = window.maptilersdk;
      if (this.userMarker) this.userMarker.remove();
      const el = document.createElement("div");
      el.className = "zl-marker zl-marker--user";
      el.setAttribute("aria-hidden", "true");
      this.userMarker = new sdk.Marker({ element: el }).setLngLat([lng, lat]).addTo(this.map);
    }

    showPopup(store, html) {
      const sdk = window.maptilersdk;
      this.closePopup();
      this.popup = new sdk.Popup({ closeButton: true, closeOnClick: false, offset: 28, maxWidth: "280px" })
        .setLngLat([store.lng, store.lat])
        .setHTML(html)
        .addTo(this.map);
    }

    closePopup() {
      if (this.popup) {
        this.popup.remove();
        this.popup = null;
      }
    }

    resize() {
      this.map && this.map.resize();
    }

    destroy() {
      this.map && this.map.remove();
    }
  }

  class LeafletAdapter {
    constructor(config) {
      this.config = config;
      this.map = null;
      this.markers = new Map();
      this.userMarker = null;
      this.popup = null;
      this.onMarkerActivate = null;
      this.onClusterActivate = null;
    }

    async init(container) {
      this.map = window.L.map(container, { scrollWheelZoom: false }).setView(
        [this.config.defaultLat, this.config.defaultLng],
        this.config.defaultZoom
      );
      if (this.config.mapProvider === "google") {
        // Real Google Maps tiles rendered as a Leaflet layer (Leaflet.GoogleMutant),
        // so the rest of this adapter (markers, clustering, popups) needs no changes.
        window.L.gridLayer.googleMutant({ type: this.config.googleMapType || "roadmap" }).addTo(this.map);
      } else {
        window.L.tileLayer(this.config.tileUrl, {
          attribution: this.config.tileAttribution,
          maxZoom: 19,
        }).addTo(this.map);
      }
      this._bindCtrlScrollZoom(container);
      return this;
    }

    // Leaflet has no built-in "ctrl + scroll to zoom" (unlike MapLibre's
    // cooperativeGestures), so plain scroll must be left alone to scroll the
    // page — only a held Ctrl/Cmd zooms the map, centered on the cursor.
    // Touch pinch-zoom needs no such gate: it already only engages the map
    // with two fingers, so it can't trap a page swipe.
    _bindCtrlScrollZoom(container) {
      const hint = document.createElement("div");
      hint.className = "zl-map-hint";
      hint.textContent = "Use ctrl + scroll to zoom the map";
      container.appendChild(hint);
      let hintTimer = null;

      container.addEventListener(
        "wheel",
        (e) => {
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            hint.classList.remove("is-visible");
            const point = this.map.mouseEventToContainerPoint(e);
            const latlng = this.map.containerPointToLatLng(point);
            const delta = e.deltaY > 0 ? -1 : 1;
            this.map.setZoomAround(latlng, this.map.getZoom() + delta, { animate: true });
          } else {
            hint.classList.add("is-visible");
            clearTimeout(hintTimer);
            hintTimer = setTimeout(() => hint.classList.remove("is-visible"), 900);
          }
        },
        { passive: false }
      );
    }

    project(store) {
      if (!this.map) return null;
      const p = this.map.latLngToContainerPoint([store.lat, store.lng]);
      return { x: p.x, y: p.y };
    }

    onMove(cb) {
      this.map.on("moveend", cb);
      this.map.on("zoomend", cb);
    }

    setMarkers(items, activeId) {
      this.markers.forEach((m) => this.map.removeLayer(m));
      this.markers.clear();

      // L.divIcon renders from an HTML *string* (el.outerHTML), which drops any
      // addEventListener already attached to `el` — only markup/attributes survive
      // serialization. So keyboard activation (Enter/Space) must be wired here, on the
      // real icon node Leaflet creates, once it's actually in the DOM (marker "add" event),
      // rather than on the throwaway `el` used only as an HTML template.
      const wireKeyboardActivation = (marker) => {
        marker.on("add", () => {
          const node = marker.getElement();
          if (!node) return;
          node.addEventListener("keydown", (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              marker.fire("click");
            }
          });
        });
      };

      const customIconUrl = this.config.customPinIconUrl;
      const customW = this.config.customPinWidth || 32;
      const customH = this.config.customPinHeight || 40;

      items.forEach((item) => {
        const L = window.L;
        if (item.count > 1) {
          const el = buildClusterEl(item.count);
          const icon = L.divIcon({ html: el.outerHTML, className: "zl-leaflet-icon", iconSize: [40, 40], iconAnchor: [20, 20] });
          const marker = L.marker([item.lat, item.lng], { icon }).addTo(this.map);
          marker.on("click", () => this.onClusterActivate && this.onClusterActivate(item));
          wireKeyboardActivation(marker);
          this.markers.set(`cluster-${item.lat}-${item.lng}`, marker);
        } else {
          const store = item.stores[0];
          const el = buildMarkerEl(store, {
            active: store.id === activeId,
            iconUrl: customIconUrl,
            iconWidth: customW,
            iconHeight: customH,
          });
          // Same custom icon uses a uniform size/anchor for every store; the
          // default teardrop pin keeps its official/partner size distinction.
          const w = customIconUrl ? customW : store.isOfficial ? 40 : 30;
          const h = customIconUrl ? customH : store.isOfficial ? 46 : 36;
          const icon = L.divIcon({
            html: el.outerHTML,
            className: "zl-leaflet-icon",
            iconSize: [w, h],
            iconAnchor: [w / 2, h],
          });
          const marker = L.marker([store.lat, store.lng], { icon, title: store.name }).addTo(this.map);
          marker.on("click", () => this.onMarkerActivate && this.onMarkerActivate(store.id));
          wireKeyboardActivation(marker);
          this.markers.set(store.id, marker);
        }
      });
    }

    flyTo(lat, lng, zoom) {
      this.map.setView([lat, lng], zoom, { animate: !Utils.prefersReducedMotion() });
    }

    fitBounds(stores, { maxZoom = 15, padding = 40 } = {}) {
      if (!stores.length) return;
      if (stores.length === 1) {
        this.flyTo(stores[0].lat, stores[0].lng, maxZoom);
        return;
      }
      const bounds = window.L.latLngBounds(stores.map((s) => [s.lat, s.lng]));
      this.map.fitBounds(bounds, { padding: [padding, padding], maxZoom });
    }

    setUserLocation(lat, lng) {
      if (this.userMarker) this.map.removeLayer(this.userMarker);
      this.userMarker = window.L.circleMarker([lat, lng], {
        radius: 7,
        weight: 2,
        color: "#111",
        fillColor: "#fff",
        fillOpacity: 1,
      }).addTo(this.map);
    }

    showPopup(store, html) {
      this.closePopup();
      // autoPan is off deliberately: we already fly/center the map on this exact
      // marker before opening the popup, so letting Leaflet auto-pan to fit the
      // popup box would just shift the pin back off-center.
      this.popup = window.L.popup({ closeButton: true, autoClose: true, autoPan: false, offset: [0, -8], maxWidth: 280 })
        .setLatLng([store.lat, store.lng])
        .setContent(html)
        .openOn(this.map);
    }

    closePopup() {
      if (this.popup) {
        this.map.closePopup(this.popup);
        this.popup = null;
      }
    }

    resize() {
      this.map && this.map.invalidateSize();
    }

    destroy() {
      this.map && this.map.remove();
    }
  }

  /* ============================================================
   * MAP MANAGER — engine-agnostic facade
   * ============================================================ */
  class MapManager {
    constructor(config) {
      this.config = config;
      this.adapter = config.useMapLibre ? new MapLibreAdapter(config) : new LeafletAdapter(config);
      this.lastStores = [];
      this.activeId = null;
      this.onSelect = null;
    }

    async init(container) {
      await this.adapter.init(container);
      this.adapter.onMarkerActivate = (id) => this.onSelect && this.onSelect(id);
      this.adapter.onClusterActivate = (cluster) => {
        // Zoom in on the cluster; if it collapses to few stores, fit to them.
        const zoom = cluster.stores.length <= 4 ? this.config.activeZoom : undefined;
        if (zoom) this.adapter.fitBounds(cluster.stores, { maxZoom: this.config.activeZoom });
        else this.adapter.flyTo(cluster.lat, cluster.lng, (this._currentZoom() || this.config.defaultZoom) + 2);
      };
      this.adapter.onMove(Utils.debounce(() => this._recluster(), 120));
      return this;
    }

    _currentZoom() {
      try {
        return this.adapter.map.getZoom();
      } catch {
        return null;
      }
    }

    _recluster() {
      if (!this.lastStores.length) return;
      const clusters = clusterByPixel(this.lastStores, (s) => this.adapter.project(s));
      this.adapter.setMarkers(clusters, this.activeId);
    }

    setStores(stores, { activeId = null, fit = true } = {}) {
      this.lastStores = stores.filter((s) => s.valid);
      this.activeId = activeId;
      this._recluster();
      if (fit && !activeId) {
        this.adapter.fitBounds(this.lastStores, { maxZoom: 14 });
      }
    }

    selectStore(store, { center = true } = {}) {
      this.activeId = store.id;
      this._recluster();
      if (center) this.adapter.flyTo(store.lat, store.lng, this.config.activeZoom);
    }

    setUserLocation(lat, lng) {
      this.adapter.setUserLocation(lat, lng);
    }

    showPopup(store, html) {
      this.adapter.showPopup(store, html);
    }

    closePopup() {
      this.adapter.closePopup();
    }

    resize() {
      this.adapter.resize();
    }

    destroy() {
      this.adapter.destroy();
    }
  }

  /* ============================================================
   * STORE LOCATOR — orchestrator per section instance
   * ============================================================ */
  class StoreLocator {
    constructor(config) {
      this.config = config;
      this.sectionId = config.sectionId;
      this.destroyed = false;
      this.userLoc = null;
      this.map = null;
      this.filterMgr = null;
      this.stores = [];
      this.suppressPopstate = false;
    }

    $(id) {
      return document.getElementById(`${id}-${this.sectionId}`);
    }

    async init() {
      const root = document.querySelector(`[data-section-id="${this.sectionId}"]`);
      if (!root) return;
      this.root = root;

      const dataEl = this.$("zl-data");
      const raw = Utils.safeJsonParse(dataEl, []);
      this.stores = (Array.isArray(raw) ? raw : []).map(normalizeStore).filter((s) => s.valid);

      this.els = {
        search: this.$("zl-search"),
        city: this.$("zl-city"),
        type: this.$("zl-type"),
        nearMe: this.$("zl-nearme"),
        reset: this.$("zl-reset"),
        count: this.$("zl-count"),
        status: this.$("zl-status"),
        results: this.$("zl-results"),
        mapContainer: this.$("zl-map"),
        viewList: this.$("zl-viewList"),
        viewMap: this.$("zl-viewMap"),
        layout: this.$("zl-layout"),
      };
      if (!this.els.mapContainer) return;

      this.filterMgr = new FilterManager(this.stores);

      this._populateFilterOptions();
      this._bindControls();

      this.map = new MapManager({
        useMapLibre: this.config.mapProvider === "maptiler",
        mapProvider: this.config.mapProvider,
        googleMapType: this.config.googleMapType,
        maptilerApiKey: this.config.maptilerApiKey,
        maptilerStyle: this.config.maptilerStyle,
        tileUrl: this.config.tileUrl,
        tileAttribution: this.config.tileAttribution,
        defaultLat: this.config.defaultLat,
        defaultLng: this.config.defaultLng,
        defaultZoom: this.config.defaultZoom,
        activeZoom: this.config.activeZoom,
        customPinIconUrl: this.config.customPinIconUrl,
        customPinWidth: this.config.customPinWidth,
        customPinHeight: this.config.customPinHeight,
      });
      await this.map.init(this.els.mapContainer);
      this.map.onSelect = (id) => this.activateStore(id, { pushUrl: true, center: true });

      window.addEventListener("popstate", () => this._applyFromUrl(false));

      this.mapEl_resizeObserver =
        typeof ResizeObserver !== "undefined"
          ? new ResizeObserver(Utils.debounce(() => this.map.resize(), 100))
          : null;
      this.mapEl_resizeObserver && this.mapEl_resizeObserver.observe(this.els.mapContainer);

      this._applyFromUrl(true);
    }

    _populateFilterOptions() {
      this.filterMgr.cities().forEach((c) => {
        const opt = document.createElement("option");
        opt.value = Utils.slugify(c);
        opt.textContent = c;
        this.els.city.appendChild(opt);
      });

      const categories = Utils.uniqSorted(this.stores.map((s) => s.category)).filter((c) => c !== "official" && c !== "retail-partner");
      const opts = [
        { value: "official", label: this.config.text.officialBadge + "s" },
        { value: "retail-partner", label: this.config.text.partnerBadge + "s" },
        ...categories.map((c) => ({ value: c, label: Utils.titleCase(c) })),
      ];
      opts.forEach(({ value, label }) => {
        const opt = document.createElement("option");
        opt.value = value;
        opt.textContent = label;
        this.els.type.appendChild(opt);
      });
    }

    _bindControls() {
      const debouncedSearch = Utils.debounce(() => {
        this.activeId = null;
        this._syncUrl({ q: this.els.search.value, store: "", replace: true });
        this._refresh();
      }, 200);
      this.els.search.addEventListener("input", debouncedSearch);

      this.els.city.addEventListener("change", () => {
        this.activeId = null;
        this._syncUrl({ city: this.els.city.value, store: "" });
        this._refresh();
      });
      this.els.type.addEventListener("change", () => {
        this.activeId = null;
        this._syncUrl({ type: this.els.type.value, store: "" });
        this._refresh();
      });

      this.els.reset.addEventListener("click", () => {
        this.els.search.value = "";
        this.els.city.value = "";
        this.els.type.value = "";
        this.userLoc = null;
        this.activeId = null;
        this._setStatus("");
        this.map.closePopup();
        this._syncUrl({ q: "", city: "", type: "", store: "" });
        this._refresh({ resetView: true });
      });

      this.els.nearMe.addEventListener("click", () => this._useMyLocation());

      if (this.els.viewList && this.els.viewMap) {
        this.els.viewList.addEventListener("click", () => this._setMobileView("list"));
        this.els.viewMap.addEventListener("click", () => this._setMobileView("map"));
      }
    }

    _setMobileView(view, onFramed) {
      this.els.layout.setAttribute("data-active-view", view);
      this.els.viewList.setAttribute("aria-pressed", String(view === "list"));
      this.els.viewMap.setAttribute("aria-pressed", String(view === "map"));
      if (view === "map") {
        // The map container is `display:none` while the List view is active, so any
        // fitBounds/flyTo computed during that time ran against a zero-size element and
        // produced a degenerate zoom. resize() alone only fixes the canvas size — it does
        // NOT recompute the framing — so we must re-frame explicitly once visible again.
        // Anything that also depends on correct sizing (e.g. opening a popup) must wait
        // for `onFramed`, or it'll compute against that same stale zero-size container.
        requestAnimationFrame(() => {
          this.map.resize();
          this._frameMap(this._lastList || []);
          onFramed && onFramed();
        });
      } else {
        onFramed && onFramed();
      }
    }

    _frameMap(list) {
      const activeStore = this.activeId ? list.find((s) => s.id === this.activeId) : null;
      if (activeStore) {
        this.map.selectStore(activeStore, { center: true });
      } else if (list.length) {
        this.map.adapter.fitBounds(list.filter((s) => s.valid), { maxZoom: 14 });
      }
    }

    _useMyLocation() {
      if (!navigator.geolocation) {
        this._setStatus("Location access isn't available in this browser. Search by city or area instead.");
        return;
      }
      this._setStatus("Detecting your location…");
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.userLoc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          this._setStatus("Showing nearest locations.");
          this.map.setUserLocation(this.userLoc.lat, this.userLoc.lng);
          this.activeId = null;
          this._refresh();
        },
        () => this._setStatus("Location access wasn't available. Search by city or area instead."),
        { enableHighAccuracy: true, timeout: 9000, maximumAge: 60000 }
      );
    }

    _setStatus(msg) {
      this.els.status.textContent = msg || "";
    }

    _syncUrl({ q, city, type, store, replace = false } = {}) {
      const params = new URLSearchParams(window.location.search);
      const set = (key, val) => {
        if (val) params.set(key, val);
        else params.delete(key);
      };
      if (q !== undefined) set("q", q);
      if (city !== undefined) set("city", city);
      if (type !== undefined) set("type", type);
      if (store !== undefined) set("store", store);

      const url = `${window.location.pathname}${params.toString() ? "?" + params.toString() : ""}`;
      this.suppressPopstate = true;
      if (replace) history.replaceState({}, "", url);
      else history.pushState({}, "", url);
    }

    _applyFromUrl(isInitial) {
      const params = new URLSearchParams(window.location.search);
      this.els.search.value = params.get("q") || "";
      this.els.city.value = params.get("city") || "";
      this.els.type.value = params.get("type") || "";
      const storeSlug = params.get("store") || "";
      this.activeId = storeSlug || null;
      this._refresh({ fromUrl: true, isInitial });
    }

    _refresh({ resetView = false, fromUrl = false, isInitial = false } = {}) {
      const list = this.filterMgr.getFilteredSorted({
        query: this.els.search.value,
        city: this.els.city.value,
        category: this.els.type.value,
        userLoc: this.userLoc,
      });
      this._lastList = list;

      this._renderResults(list);

      const activeStore = this.activeId ? list.find((s) => s.id === this.activeId) : null;
      this.map.setStores(list, { activeId: activeStore ? activeStore.id : null, fit: !activeStore });

      if (resetView) {
        this.map.adapter.flyTo(this.config.defaultLat, this.config.defaultLng, this.config.defaultZoom);
      } else if (activeStore) {
        const shouldCenter = !fromUrl || isInitial;
        const openInitialPopup = fromUrl && isInitial ? () => this.map.showPopup(activeStore, this._popupHtml(activeStore)) : null;
        // Same hidden-0×0-map hazard as activateStore(): a deep link (?store=)
        // opened on mobile lands in List view, so switch to Map first and defer
        // centering/popup until _setMobileView confirms it's actually visible.
        if (shouldCenter && this.els.layout.getAttribute("data-active-view") !== "map") {
          this._setMobileView("map", openInitialPopup || undefined);
        } else {
          if (shouldCenter) this.map.selectStore(activeStore, { center: true });
          openInitialPopup && openInitialPopup();
        }
      }
    }

    _popupHtml(store) {
      const t = this.config.text;
      const d = this.config.display;
      const phone = d.showPhone ? store.phone : "";
      const directionsUrl = buildDirectionsUrl(store);
      return `
        <div class="zl-popup">
          <p class="zl-popup__name">${Utils.escapeHtml(store.name)}</p>
          <p class="zl-popup__addr">${Utils.escapeHtml(store.address)}${store.city ? `, ${Utils.escapeHtml(store.city)}` : ""}</p>
          ${phone ? `<a class="zl-popup__phone" href="tel:${Utils.escapeHtml(phone)}">${Utils.escapeHtml(phone)}</a>` : ""}
          <a class="zl-popup__directions" href="${Utils.escapeHtml(directionsUrl)}" target="_blank" rel="noopener noreferrer">${Utils.escapeHtml(t.directions)}</a>
        </div>
      `;
    }

    _renderResults(list) {
      this.els.count.textContent = `${list.length} location${list.length === 1 ? "" : "s"}`;

      if (!list.length) {
        this.els.results.innerHTML = `
          <li class="zl-empty">
            <p class="zl-empty__title">${Utils.escapeHtml(this.config.text.emptyHeading)}</p>
            <p class="zl-empty__body">${Utils.escapeHtml(this.config.text.emptyBody)}</p>
            <button type="button" class="zl-btn zl-btn--secondary" data-zl-empty-reset>Reset filters</button>
          </li>`;
        const btn = this.els.results.querySelector("[data-zl-empty-reset]");
        btn && btn.addEventListener("click", () => this.els.reset.click());
        return;
      }

      const d = this.config.display;

      this.els.results.innerHTML = list
        .map((s) => {
          const isActive = s.id === this.activeId;
          const badgeClass = s.isOfficial ? "zl-badge--official" : "zl-badge--partner";
          const badgeLabel = s.isOfficial ? this.config.text.officialBadge : this.config.text.partnerBadge;
          const status = d.showHours ? computeOpenStatus(s.hours) : null;
          const dist = d.showDistance && this.userLoc && Number.isFinite(s.distanceKm) ? Utils.formatDistance(s.distanceKm) : "";
          const phone = d.showPhone ? s.phone : "";

          return `
          <li class="zl-card ${isActive ? "is-active" : ""}" data-id="${Utils.escapeHtml(s.id)}">
            <button type="button" class="zl-card__select" aria-current="${isActive ? "true" : "false"}">
              <span class="zl-card__top">
                <span class="zl-card__name">${Utils.escapeHtml(s.name)}</span>
                ${d.showOfficialBadge ? `<span class="zl-badge ${badgeClass}">${Utils.escapeHtml(badgeLabel)}</span>` : ""}
              </span>
              <span class="zl-card__addr">${Utils.escapeHtml(s.address)}${s.city ? `, ${Utils.escapeHtml(s.city)}` : ""}</span>
              <span class="zl-card__meta">
                ${status ? `<span class="${status.isOpen ? "is-open" : ""}">${Utils.escapeHtml(status.label)}</span>` : ""}
                ${dist ? `<span>${Utils.escapeHtml(dist)}</span>` : ""}
              </span>
            </button>
            <span class="zl-card__actions">
              <a class="zl-link" href="${Utils.escapeHtml(buildDirectionsUrl(s))}" target="_blank" rel="noopener noreferrer">${Utils.escapeHtml(this.config.text.directions)}</a>
              ${phone ? `<a class="zl-link" href="tel:${Utils.escapeHtml(phone)}">${Utils.escapeHtml(this.config.text.call)}</a>` : ""}
              <button type="button" class="zl-link zl-link--btn" data-zl-details>${Utils.escapeHtml(this.config.text.details)}</button>
            </span>
          </li>`;
        })
        .join("");

      this.els.results.querySelectorAll(".zl-card").forEach((card) => {
        const id = card.getAttribute("data-id");
        const selectBtn = card.querySelector(".zl-card__select");
        selectBtn.addEventListener("click", () => this.activateStore(id, { pushUrl: true, center: true }));
        const detailsBtn = card.querySelector("[data-zl-details]");
        detailsBtn &&
          detailsBtn.addEventListener("click", () => this.activateStore(id, { pushUrl: true, center: true }));
      });

      if (this.activeId) {
        const activeCard = this.els.results.querySelector(`.zl-card[data-id="${CSS.escape(this.activeId)}"]`);
        activeCard && activeCard.scrollIntoView({ block: "nearest", behavior: Utils.prefersReducedMotion() ? "auto" : "smooth" });
      }
    }

    activateStore(id, { pushUrl = false, center = true } = {}) {
      const store = this.stores.find((s) => s.id === id);
      if (!store) return;
      this.activeId = id;
      if (pushUrl) this._syncUrl({ store: id });
      this._refresh();
      const openPopup = () => this.map.showPopup(store, this._popupHtml(store));

      if (center) {
        // On mobile the map pane can be hidden behind the List view. Flying to
        // (or opening a popup on) a hidden 0×0 map computes against bogus
        // dimensions, so switch view first and only center + open the popup
        // once _setMobileView's resize + re-frame confirms it's actually visible.
        if (this.els.layout.getAttribute("data-active-view") !== "map") {
          this._setMobileView("map", openPopup);
        } else {
          this.map.selectStore(store, { center: true });
          openPopup();
        }
      } else {
        openPopup();
      }
    }

    destroy() {
      this.destroyed = true;
      this.mapEl_resizeObserver && this.mapEl_resizeObserver.disconnect();
      this.map && this.map.destroy();
    }
  }

  /* ============================================================
   * BOOT — queue drain + Theme Editor lifecycle guards
   * ============================================================ */
  const instances = new Map();

  function boot(config) {
    const root = document.querySelector(`[data-section-id="${config.sectionId}"]`);
    if (!root || root.dataset.zlInitialized === "1") return;
    root.dataset.zlInitialized = "1";

    const instance = new StoreLocator(config);
    instances.set(config.sectionId, instance);
    instance.init();
  }

  function waitForLibs(config, tries = 0) {
    const needsMapLibre = config.mapProvider === "maptiler";
    const needsGoogle = config.mapProvider === "google";
    const mapReady = needsMapLibre
      ? !!window.maptilersdk
      : needsGoogle
      ? !!(window.L && window.L.GridLayer && window.L.GridLayer.GoogleMutant && window.google && window.google.maps)
      : !!window.L;
    const fuseReady = !!window.Fuse;

    if (mapReady && fuseReady) {
      boot(config);
      return;
    }
    // Google's script is an external, network-dependent load unlike the bundled
    // CDN libs, so give it more time (200 tries × 50ms = 10s) before giving up.
    const maxTries = needsGoogle ? 200 : 100;
    if (tries > maxTries) {
      console.error("[ZL] Store Locator: required libraries failed to load.");
      return;
    }
    setTimeout(() => waitForLibs(config, tries + 1), 50);
  }

  Utils.ready(function () {
    (window.ZL_LOCATOR_QUEUE || []).forEach((config) => waitForLibs(config));
    window.ZL_LOCATOR_QUEUE = { push: (config) => waitForLibs(config) };
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const id = event.target?.getAttribute?.("data-section-id");
    const instance = id && instances.get(id);
    if (instance) {
      instance.destroy();
      instances.delete(id);
    }
  });

  document.addEventListener("shopify:section:load", (event) => {
    const id = event.target?.getAttribute?.("data-section-id");
    const section = id && document.querySelector(`[data-section-id="${id}"]`);
    if (section) section.dataset.zlInitialized = "";
  });
})();
